'use client';

import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import * as Yup from 'yup';
import { faPhone } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useCustomSearchParams, useFormApi } from '@lamesarv-sdk/hooks';
import { phoneRegex } from '@lamesarv-sdk/tools';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { useDevice } from '@/hooks/useDevice';
import { parsePhone } from '@/tools/dynamics';
import { getNumberIdsFromString, isFormDynamicsLocationEnabled } from '@/tools/forms';

import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { IFormMetadataProps } from '@/components/Form/types';
import { Modal } from '@/components/Modal/Modal';

import { IFormInput, InventoryLocations } from '@lamesarv-sdk/types';
import { DynamicsLeadSource, DynamicsLeadType } from '@/types/dynamicsForms';
import { IComponentGenericFormField } from '@/types/cms';
import { FormTag } from '@/types/forms';
import { IGenericInquiryFormProps } from './types';

import { StyleClassicForm } from './Styles/Classic';
import { StyleSimpleForm } from './Styles/Simple';

interface IInventoryForm extends IFormMetadataProps {
  leadType: string;
  leadSource: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  contact: string;
  product: string;
  comments: string;
  terms: boolean;
  subscribe: boolean;
}

// --

const FormReset = ({ wasSuccessful }: { wasSuccessful: boolean }) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!wasSuccessful) return;

    reset();
    reset({
      phone: '',
    });
  }, [wasSuccessful]);

  return null;
};

const formCaptchaErrorMessage = () => {
  return (
    <div className="w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-4 mt-4 text-center justify-center text-red-600 bg-red-100 md:border border-red-300 rounded-lg">
      <div className="w-full flex flex-col md:flex-row gap-1 md:gap-2 items-center justify-center">
        <span>Form validation failed.</span>
        <span className="flex flex-row gap-2 items-center">
          Please Call{' '}
          <a href="tel:877-641-8052" className="flex gap-2 items-center decoration-dashed underline underline-offset-4">
            <FontAwesomeIcon icon={faPhone} className="size-3.5" />
            <span>877-641-8052</span>
          </a>
        </span>
        <span>for your inquiry</span>
      </div>
    </div>
  );
};

// --

const getHashSanitized = (hash?: string) => {
  if (!hash || hash?.trim() === '#') return undefined;
  return hash.replaceAll('#', '').trim().toLowerCase();
};

const removeHash = () => {
  if (typeof window === 'undefined') return;
  if ('replaceState' in window.history) {
    window.history.replaceState('', window.document.title, window.location.pathname + window.location.search);
  }
};

// --

const getYupStringSchema = (item: IComponentGenericFormField, label: string) => {
  return item.required ? Yup.string().required(`${label} is required`) : Yup.string().optional();
};

const mapFormFields = (fieldsBase: IComponentGenericFormField[], includeTerms?: boolean) => {
  const fields: { [field: string]: IComponentGenericFormField } = {};
  const fieldsSchema: Record<string, Yup.AnySchema> = {};

  const fieldMapping: Record<string, { key: string; label: string }> = {
    'first-name': { key: 'firstName', label: 'First Name' },
    'last-name': { key: 'lastName', label: 'Last Name' },
    comments: { key: 'comments', label: 'Comments' },
    'preferred-location': { key: 'location', label: 'Location' },
    'preferred-contact': { key: 'contact', label: 'Contact' },
    product: { key: 'product', label: 'Product' },
    email: { key: 'email', label: 'Email' },
    phone: { key: 'phone', label: 'Phone number' },
  };

  fieldsBase.forEach((item) => {
    fields[item.type] = item;

    const fieldMapped = fieldMapping[item.type];

    if (fieldMapped) {
      if (item.type === 'email') {
        fieldsSchema[fieldMapped.key] = item.required
          ? Yup.string().email('Invalid email').required(`${fieldMapped.label} is required`)
          : Yup.string().email('Invalid email').optional();
      } else if (item.type === 'phone') {
        fieldsSchema[fieldMapped.key] = item.required
          ? Yup.string()
              .required(`${fieldMapped.label} is required`)
              .matches(phoneRegex, 'Please enter a valid phone number.')
          : Yup.string().optional();
      } else {
        fieldsSchema[fieldMapped.key] = getYupStringSchema(item, fieldMapped.label);
      }
    } else {
      fieldsSchema[item.type] = Yup.mixed().optional();
    }
  });

  if (includeTerms) {
    fieldsSchema.terms = Yup.boolean().oneOf([true], 'You must accept the terms and conditions');
  }

  return {
    fields,
    schema: Yup.object().shape(fieldsSchema),
  };
};

const getLegacyZipCode = (location: string, inventoryLocations: InventoryLocations) => {
  const locationItem = Object.values(inventoryLocations).find((item) =>
    item.address.toLowerCase().includes(location.toLowerCase()),
  );

  let zipCode = locationItem?.address?.trim().split(' ').pop() || '';
  if (zipCode.length !== 5) zipCode = '';

  return zipCode;
};

const mapStyles = {
  simple: StyleSimpleForm,
  classic: StyleClassicForm,
};

const FormBase = (props: IGenericInquiryFormProps) => {
  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();
  const { fetchData: getDevice } = useDevice({ metadata: true, skipFetch: true });
  const { context } = useContext(LayoutContext);

  const { env } = useEnv();

  const { fields, schema } = mapFormFields(props.fields, props.display_terms_checkbox);

  const _loading = loading || loadingLegacy || false;
  const _error = error || errorLegacy || false;
  const _submitted = submitted || submittedLegacy || false;

  const wasSuccessful = !_loading && !_error && _submitted;
  const wasError = !_loading && _error && _submitted;

  const onSubmit = async (data: IInventoryForm, metadata?: Record<string, unknown>) => {
    const formMappings = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: props.custom_form_leadtype
          ? props.custom_form_leadtype === 'buyer'
            ? String(DynamicsLeadType.buyer)
            : String(DynamicsLeadType.seller)
          : String(DynamicsLeadType.seller),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource.website),
      },
      {
        DataverseFieldName: 'firstname',
        DataverseFieldValue: data.firstName || '',
      },
      {
        DataverseFieldName: 'lastname',
        DataverseFieldValue: data.lastName || '',
      },
      {
        DataverseFieldName: 'emailaddress1',
        DataverseFieldValue: data.email || '',
      },
      {
        DataverseFieldName: 'mobilephone',
        DataverseFieldValue: data.phone ? parsePhone(data.phone) : '',
      },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue: String(
          props.locationsOptionSet.find((item) => item.Label === data.location)?.DataverseValue ?? '',
        ),
      },
      {
        DataverseFieldName: 'preferredcontactmethodcode',
        DataverseFieldValue: data.contact || '',
      },
      {
        DataverseFieldName: 'lmrv_product',
        DataverseFieldValue: data.product || '',
      },
      {
        DataverseFieldName: 'lmrv_comments',
        DataverseFieldValue: data.comments || '',
      },
      {
        DataverseFieldName: 'lmrv_accepttermsandconditions',
        DataverseFieldValue: data.terms ? '1' : '0',
      },
      {
        DataverseFieldName: 'lmrv_subscribe',
        DataverseFieldValue: data.subscribe ? '1' : '0',
      },
      {
        DataverseFieldName: 'lmrv_url',
        DataverseFieldValue: typeof location !== 'undefined' ? location?.href : '',
      },
      {
        DataverseFieldName: 'crbdb_segment',
        DataverseFieldValue: props.dynamics_segment,
      },
    ];

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      const device = await getDevice();
      data.ip = device.ip;
      data.user_agent = device.userAgent;
      data.platform = device.platform;
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!context.geoLocation) {
      data.geo_coordinates = context.geoLocation.join(',');
    }

    if (env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE && env.ENABLE_FORMS_CAPTCHA) {
      data.captcha_score = metadata?.captchaScore ? Number(metadata?.captchaScore) : undefined;
      data.captcha_threshold = metadata?.captchaThreshold ? Number(metadata?.captchaThreshold) : undefined;
    }

    if (env.ENABLE_FORMS_DYNAMICS && data.location && isFormDynamicsLocationEnabled(env, data.location)) {
      await sendForm({
        formId: `#${props.custom_id || 'genericInquiryForm'}`,
        mappings: formMappings,
        dynamicsFormId: env.DYNAMICS_FORM_ID_INQUIRY || props.custom_dynamics_id,
      });
    }

    if (env.ENABLE_FORMS_LEGACY) {
      const customTagIds = getNumberIdsFromString(props.custom_active_campaign_tag_ids);
      const customListIds = getNumberIdsFromString(props.custom_active_campaign_list_ids);

      sendFormLegacy({
        ...data,
        zipCode: getLegacyZipCode(data?.location ?? '', context.inventoryLocations),
        stockNumber: undefined,
        emailType: undefined,
        comments: `Preferred Location: ${data?.location}\n${data?.comments || ''}`,
        extra_tag_ids: [...customTagIds, FormTag.GENERIC_INQUIRY],
        extra_list_ids: customListIds,
      } as unknown as IFormInput);
    }
  };

  useEffect(() => {
    if (wasSuccessful && props.onSuccess) {
      props.onSuccess();
    }
  }, [wasSuccessful]);

  const FormStyled = mapStyles[props.style] ?? mapStyles.simple;

  // -- Prevent rendering if neither form system is enabled
  if (!env.ENABLE_FORMS_DYNAMICS && !env.ENABLE_FORMS_LEGACY) {
    return null;
  }

  return (
    <CommonForm
      id={props.custom_id || 'genericInquiryForm'}
      schema={schema}
      onSubmit={onSubmit}
      isDisabled={_loading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-inquiry` : 'form-inquiry'}
      className={twMerge('font-helvetica', props.modal ? '' : 'py-[40px] md:py-[50px]', props.classes_container)}
      customCaptchaErrorMessage={formCaptchaErrorMessage}
    >
      <CommonHidden name="formId" value={props.custom_active_campaign_id || '23'} />
      <CommonHidden name="formType" value={props.custom_form_type || 'custom'} />
      <CommonHidden name="leadType" value={props.custom_form_leadtype || 'seller'} />
      <CommonHidden name="leadSource" value={props.custom_form_leadsource || 'lamesarv'} />
      <FormStyled
        {...props}
        fieldsMapped={fields}
        isDisabled={_loading || wasSuccessful}
        wasError={wasError}
        wasSuccessful={wasSuccessful}
      />
      <FormReset wasSuccessful={wasSuccessful} />
    </CommonForm>
  );
};

export const GenericInquiryForm = (props: IGenericInquiryFormProps) => {
  const { searchParams } = useCustomSearchParams();
  const [modalOpen, setModalOpen] = useState(false);

  const hashParameter = getHashSanitized(props?.modal_hash_parameter);

  const onHashChanged = () => {
    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);

      if (hashParameter) {
        setModalOpen(hashLocation === hashParameter);
      }
    }
  };

  useEffect(() => {
    let modalOpenStatus: boolean = false;

    if (props.modal && props.modal_url_parameter && !!searchParams.get(props.modal_url_parameter)) {
      modalOpenStatus = true;
    }

    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);
      const hashParameter = getHashSanitized(props?.modal_hash_parameter);

      if (!!hashParameter && hashLocation === hashParameter) {
        modalOpenStatus = true;
      }
    }

    setModalOpen(modalOpenStatus);

    // Hash Change Control
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', onHashChanged);
      return () => {
        window.removeEventListener('hashchange', onHashChanged);
      };
    }
  }, []);

  const onClose = () => {
    setModalOpen(false);
    if (hashParameter) {
      removeHash();
    }
  };

  const onSuccess = () => {
    setTimeout(() => {
      setModalOpen(false);
      if (hashParameter) {
        removeHash();
      }
    }, 2000);
  };

  if (props.modal) {
    return (
      <Modal isOpen={modalOpen} onClose={onClose} useMaxWidth={false} classesClose="right-2 top-2 md:right-8 md:top-8">
        <FormBase
          {...props}
          onSuccess={onSuccess}
          classes={twMerge('md:w-full md:max-w-[720px] xl:w-full xl:max-w-[865px] border-none', props.classes)}
        />
      </Modal>
    );
  }

  return <FormBase {...props} />;
};
